import { useEffect, Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Features from './components/Features';
import Tools from './components/Tools';
import Table from './components/Table';
import Testimonials from './components/Testimonials';
import Faq from './components/Faq';
import Footer from './components/Footer';
import SignUp from './pages/SignUp';
import Purchase from './pages/Purchase';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import NotFound from './pages/404';
import IaParaAdvogados from './pages/ia-para-advogados';
import LawxLegaltechBrasil from './pages/LawxLegaltechBrasil';
import Calc from './pages/Calc';
import Apresentacao from './pages/Apresentacao';
import ApresentacaoConfig from './pages/ApresentacaoConfig';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Admin from './pages/Admin';
import SEO from './components/SEO';

// Lazy loading para CRM - só carrega quando necessário
const CrmOptimized = lazy(() => import('./pages/CrmOptimized'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
import { TRACKING_API_URL } from './config';

function HomePage() {
  useEffect(() => {
    // Track PageView event
    fetch(TRACKING_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_name: 'PageView',
      }),
    })
      .then(() => console.log('PageView event tracked'))
      .catch((error) => console.error('Error tracking PageView event:', error));
  }, []);

  return (
    <div className="bg-[#081428] text-white min-h-screen">
      <SEO
        title="A melhor IA para advogados"
        description="Otimize sua prática jurídica com a melhor IA para advogados. A inteligência artificial para advogado. Se concentre mais no que realmente importa. Descubra o verdadeiro poder jurídico ai"
        keywords="IA para advogados, inteligência artificial jurídica, legaltech, automação jurídica, ferramentas para advogados"
        url="/"
      />
      <Navbar />
      <Hero />
      {/* <LeadForm /> */}
      <Features />
      <Tools />
      <Table />
      <Testimonials />
      <Faq />
      <Footer />
    </div>
  );
}

// Componente interno que tem acesso ao contexto de auth
function AppContent() {
  const { signOut } = useAuth();

  // Interceptar F5 e outros atalhos de refresh
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // F5 - fazer logout
      if (event.key === 'F5') {
        event.preventDefault();
        console.log('🚪 F5 pressionado - fazendo logout');
        signOut();
        return false;
      }
      // Ctrl+R ou Ctrl+F5 - apenas bloquear
      if ((event.ctrlKey && (event.key === 'r' || event.key === 'R')) ||
          (event.ctrlKey && event.key === 'F5')) {
        event.preventDefault();
        console.log('🔄 Refresh bloqueado - use os botões do sistema');
        return false;
      }
    };

    // Adicionar listener
    document.addEventListener('keydown', handleKeyDown);
    
    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [signOut]);

  return (
    <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/purchase" element={<Purchase />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms-of-use" element={<Terms />} />
        <Route path="/ia-para-advogados" element={<IaParaAdvogados />} />
        <Route path="/2024/08/29/lawx-legaltech-brasil" element={<LawxLegaltechBrasil />} />
        <Route path="/calc" element={<Calc />} />
        <Route path="/apresentacao" element={<Apresentacao />} />
        <Route path="/apresentacao/:code" element={<Apresentacao />} />
        <Route path="/apresentacao/config" element={<ApresentacaoConfig />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route 
          path="/crm" 
          element={
            <ProtectedRoute>
              <Suspense fallback={
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Carregando CRM...</p>
                  </div>
                </div>
              }>
                <CrmOptimized />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/calendar" 
          element={
            <ProtectedRoute>
              <Suspense fallback={
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Carregando Calendário...</p>
                  </div>
                </div>
              }>
                <CalendarPage />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
